import { BridgeClient } from 'app/service/bridge/BridgeClient';

const LIST_PAGE_SIZE = 500;
const MUTATION_CONCURRENCY = 8;
const pairKey = (term, synonym) => `${term}\u0000${synonym}`;

async function runBounded(items, worker, concurrency = MUTATION_CONCURRENCY) {
  for (let offset = 0; offset < items.length; offset += concurrency) {
    const results = await Promise.all(items.slice(offset, offset + concurrency).map(item => (
      Promise.resolve()
        .then(() => worker(item))
        .then(() => ({ ok: true }), error => ({ ok: false, error }))
    )));
    const failure = results.find(result => !result.ok);
    if (failure) throw failure.error;
  }
}

/**
 * Creates a synonyms API backed by the bridge transport (postMessage → parent → plugin backend).
 *
 * Uses BridgeClient.sendMessageWithResponse to send feature actions through the parent bridge.
 * Parent bridge forwards unknown message types to adapter.callFeatureAction,
 * which POSTs to bridge.php → BridgeController → BridgeUseCase.
 *
 * Data model translation:
 *   Admin app uses: { _id, left: string[], right: string[], bidirectional, enabled, source }
 *   Bridge core (DbSynonymStorage) returns: { id_synonym, term, synonym, is_active }
 *
 *   The admin model supports synonym GROUPS (left: ['shoes','sneakers'], right: ['boots']),
 *   but the plugin core stores flat (term, synonym) pairs per row. This adapter expands
 *   groups into their left×right cartesian product on save, and returns individual pairs
 *   on load. Groups created via the legacy cloud service will appear as separate entries
 *   after a bridge load — this is a known, acceptable semantic narrowing.
 *
 *   Bidirectional synonyms are stored as two mirror rows (phone→mobile + mobile→phone).
 *   load() collapses mirror pairs into a single bidirectional entry.
 *   save() does a full diff: deletes removed rows, upserts current ones, sets disabled state.
 */
export function createBridgeSynonymsApi() {
  const client = new BridgeClient();
  const runOperations = operations => runBounded(
    operations,
    operation => client.sendMessageWithResponse(operation.type, operation.payload),
  );

  async function listAll() {
    const items = [];
    let offset = 0;

    while (true) {
      const response = await client.sendMessageWithResponse('synonyms.list', {
        limit: LIST_PAGE_SIZE,
        offset,
      });
      const page = response.data || [];
      items.push(...page);
      if (page.length < LIST_PAGE_SIZE) break;
      offset += page.length;
    }

    return items;
  }

  /**
   * Expand an admin synonym entry into flat (term, synonym) pairs.
   * { left: ['a','b'], right: ['x','y'] } → [['a','x'], ['a','y'], ['b','x'], ['b','y']]
   */
  function expandPairs(syn) {
    var lefts = Array.isArray(syn.left) ? syn.left : [syn.left];
    var rights = Array.isArray(syn.right) ? syn.right : [syn.right];
    var pairs = [];
    for (var l = 0; l < lefts.length; l++) {
      for (var r = 0; r < rights.length; r++) {
        pairs.push([lefts[l], rights[r]]);
      }
    }
    return pairs;
  }

  return {
    /**
     * @param {string} _shopId — not used by bridge (context comes from plugin side), kept for interface parity
     * @returns {Promise<{ synonyms: Array }>}
     */
    async load(_shopId) {
      // Bridge returns { result, data: [ { id_synonym, term, synonym, is_active, ... } ], meta }
      // Bidirectional pairs appear as two rows: (A→B) and (B→A). Collapse them into one entry.
      const items = await listAll();
      const itemKeys = new Set(items.map(item => pairKey(item.term, item.synonym)));
      const seen = new Set();
      const synonyms = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const key = pairKey(item.term, item.synonym);

        if (seen.has(key)) continue;

        const mirrorKey = pairKey(item.synonym, item.term);
        const hasMirror = itemKeys.has(mirrorKey);

        seen.add(key);
        if (hasMirror) seen.add(mirrorKey);

        synonyms.push({
          _id: String(item.id_synonym),
          left: [item.term],
          right: [item.synonym],
          bidirectional: hasMirror,
          enabled: Boolean(item.is_active),
          source: 'manual',
        });
      }

      return { synonyms };
    },

    /**
     * Full-diff save: compares incoming admin state against current DB rows,
     * deletes removed rows, upserts current ones, and sets disabled state.
     * Multi-value left/right arrays are expanded to their cartesian product.
     *
     * @param {string} _shopId — not used by bridge
     * @param {Array} synonyms — admin-format synonym list
     * @returns {Promise<{ synonyms: Array }>}
     */
    async save(_shopId, synonyms) {
      // 1. Fetch current state to compute diff
      const currentItems = await listAll();

      // 2. Build the desired flat rows and retain the source relations so one
      //    bidirectional upsert can create both mirror rows.
      const currentByPair = new Map(
        currentItems.map(item => [pairKey(item.term, item.synonym), item]),
      );
      const expectedPairs = new Map();
      const sourcePairs = [];
      for (var i = 0; i < synonyms.length; i++) {
        var pairs = expandPairs(synonyms[i]);
        for (var p = 0; p < pairs.length; p++) {
          const desiredPair = {
            term: pairs[p][0],
            synonym: pairs[p][1],
            bidirectional: Boolean(synonyms[i].bidirectional),
            active: synonyms[i].enabled !== false,
          };
          sourcePairs.push(desiredPair);
          expectedPairs.set(pairKey(desiredPair.term, desiredPair.synonym), desiredPair);
          if (synonyms[i].bidirectional) {
            expectedPairs.set(pairKey(desiredPair.synonym, desiredPair.term), {
              term: desiredPair.synonym,
              synonym: desiredPair.term,
              bidirectional: true,
              active: desiredPair.active,
            });
          }
        }
      }

      // 3. Prepare deletes, but run them last so a failed upsert cannot erase working data.
      var deleteOps = [];
      for (var d = 0; d < currentItems.length; d++) {
        var cur = currentItems[d];
        if (!expectedPairs.has(pairKey(cur.term, cur.synonym))) {
          const deletePayload = { term: cur.term, synonym: cur.synonym };
          deleteOps.push({ type: 'synonyms.delete', payload: deletePayload });
        }
      }

      // 4. Upsert only missing rows. Rewriting every existing row makes a CSV
      //    import increasingly slow as the synonym dictionary grows.
      var upsertOps = [];
      const upsertedKeys = new Set();
      for (var u = 0; u < sourcePairs.length; u++) {
        var sourcePair = sourcePairs[u];
        const forwardKey = pairKey(sourcePair.term, sourcePair.synonym);
        const reverseKey = pairKey(sourcePair.synonym, sourcePair.term);
        const forwardExists = currentByPair.has(forwardKey) || upsertedKeys.has(forwardKey);
        const reverseExists = !sourcePair.bidirectional
          || currentByPair.has(reverseKey)
          || upsertedKeys.has(reverseKey);

        if (!forwardExists || !reverseExists) {
          const upsertPayload = {
            term: sourcePair.term,
            synonym: sourcePair.synonym,
            bidirectional: sourcePair.bidirectional,
          };
          upsertOps.push({ type: 'synonyms.upsert', payload: upsertPayload });
          upsertedKeys.add(forwardKey);
          if (sourcePair.bidirectional) upsertedKeys.add(reverseKey);
        }
      }
      await runOperations(upsertOps);

      // 5. Change active state only where needed. A fresh upsert always creates
      //    an active row, so disabled imports are switched off afterwards.
      var activeOps = [];
      expectedPairs.forEach((desired, key) => {
        const current = currentByPair.get(key);
        const currentActive = upsertedKeys.has(key) ? true : Boolean(current?.is_active);
        if (currentActive !== desired.active) {
          activeOps.push({
            type: 'synonyms.set_active',
            payload: {
              term: desired.term,
              synonym: desired.synonym,
              active: desired.active,
            },
          });
        }
      });
      await runOperations(activeOps);
      await runOperations(deleteOps);

      // Re-fetch to return authoritative server state
      return this.load();
    },
  };
}
